import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Send, Check, Clock, Code2, AlignLeft, Copy, Pencil, CalendarClock, X } from "lucide-react";
import EmailPreview from "@/components/EmailPreview";
import ScheduleCampaignDialog from "@/components/ScheduleCampaignDialog";

const Stat = ({ label, value, accent, testid }) => (
  <div className="cf-card p-5 flex flex-col" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// {label}</div>
    <div className={`font-display text-4xl font-bold mt-3 tracking-tight text-right tabular-nums ${accent ? "text-[#E23A28]" : ""}`}>{value}</div>
  </div>
);

const VariantCard = ({ label, stats, sent }) => {
  const openRate = sent > 0 ? Math.round((stats.opened / sent) * 100) : 0;
  const replyRate = sent > 0 ? Math.round((stats.replied / sent) * 100) : 0;
  return (
    <div className="cf-card p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// variant {label}</span>
        <span className="cf-badge cf-badge-accent">{label}</span>
      </div>
      <div className="font-mono text-xs text-zinc-400 mb-4">{sent} sent</div>
      <div className="grid grid-cols-2 gap-4">
        <div><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Open rate</div><div className="font-display text-3xl font-bold mt-1">{openRate}%</div><div className="font-mono text-xs text-zinc-500 mt-1">{stats.opened} opens</div></div>
        <div><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Reply rate</div><div className="font-display text-3xl font-bold mt-1">{replyRate}%</div><div className="font-mono text-xs text-zinc-500 mt-1">{stats.replied} replies</div></div>
      </div>
    </div>
  );
};

export default function CampaignDetail() {
  const { id } = useParams();
  const [camp, setCamp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewStep, setPreviewStep] = useState(0);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  const load = () => api.get(`/campaigns/${id}`).then((r) => setCamp(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const onSend = async () => {
    if (!window.confirm("Send this campaign?")) return;
    setBusy(true);
    try { await api.post(`/campaigns/${id}/send`); toast.success("Campaign dispatched"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); } finally { setBusy(false); }
  };

  const onSchedule = async (payload) => {
    setScheduling(true);
    try { await api.post(`/campaigns/${id}/schedule`, payload); toast.success("Campaign scheduled"); setScheduleOpen(false); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Schedule failed"); } finally { setScheduling(false); }
  };

  const onUnschedule = async () => {
    if (!window.confirm("Cancel this schedule?")) return;
    try { await api.post(`/campaigns/${id}/unschedule`); toast.success("Schedule cancelled"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const onExport = async () => {
    try {
      const res = await api.get(`/campaigns/${id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${camp.name.replace(/[^a-z0-9]/gi, "_")}_stats.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  const onMarkReplied = async (sendId) => {
    try { await api.post(`/track/reply/${sendId}`); toast.success("Marked as replied"); load(); } catch { toast.error("Failed"); }
  };

  const onDuplicate = async () => {
    try { const { data } = await api.post(`/campaigns/${id}/duplicate`); toast.success("Duplicated"); window.location.href = `/campaigns/${data.id}/edit`; }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (!camp) return <div className="p-12 text-zinc-500 font-mono text-sm">// loading...</div>;

  const s = camp.stats || {};
  const variantStats = camp.stats_variant || { A: {}, B: {} };
  const isSent = camp.status === "sent";
  const sends = camp.sends || [];
  const steps = camp.steps || [];

  return (
    <div className="p-8 lg:p-10 max-w-[1400px] cf-fade">
      <Link to="/campaigns" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-2 mb-4"><ArrowLeft size={12} /> Back to campaigns</Link>
      <div className="flex items-end justify-between mb-8 pb-6 border-b border-zinc-800 flex-wrap gap-6">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 truncate" title={camp.list_name}>// campaign / {camp.list_name}</div>
          <h1 className="cf-page-title mt-2 break-words" data-testid="campaign-detail-name" title={camp.name}>{camp.name}</h1>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <span className={`cf-badge ${isSent ? "cf-badge-success" : "cf-badge-warn"}`}>{camp.status}</span>
            {camp.ab_test_enabled && <span className="cf-badge cf-badge-accent">A/B test</span>}
            <span className="font-mono text-xs text-zinc-500">{steps.length} step{steps.length === 1 ? "" : "s"}</span>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          {!isSent && <Link to={`/campaigns/${id}/edit`} data-testid="edit-campaign-button" className="cf-btn-secondary"><Pencil size={14} /> Edit</Link>}
          <button onClick={onDuplicate} data-testid="duplicate-campaign-button" className="cf-btn-secondary"><Copy size={14} /> Duplicate</button>
          {camp.status === "scheduled" && <button onClick={onUnschedule} data-testid="unschedule-campaign-button" className="cf-btn-secondary"><X size={14} /> Cancel Schedule</button>}
          {!isSent && <button onClick={() => setScheduleOpen(true)} data-testid="open-schedule-dialog-button" className="cf-btn-secondary"><CalendarClock size={14} /> {camp.status === "scheduled" ? "Reschedule" : "Schedule"}</button>}
          {!isSent && <button onClick={onSend} disabled={busy} data-testid="send-campaign-button" className="cf-btn-primary"><Send size={14} /> {busy ? "Dispatching..." : "Send Now"}</button>}
          {isSent && <button onClick={onExport} data-testid="export-csv-button" className="cf-btn-primary"><Download size={14} /> Export Stats (CSV)</button>}
        </div>
      </div>

      {camp.status === "scheduled" && camp.schedule_summary && (
        <div className="mb-6 p-4 border flex items-start gap-3" style={{ borderColor: "color-mix(in srgb, var(--cf-accent) 40%, transparent)", background: "var(--cf-surface)" }} data-testid="schedule-banner">
          <CalendarClock size={16} style={{ color: "var(--cf-accent)" }} className="mt-0.5 flex-shrink-0" />
          <div className="min-w-0"><div className="font-mono text-[10px] uppercase tracking-widest cf-text-dim">// dispatch scheduled</div><div className="cf-text-base text-sm mt-0.5 break-words">{camp.schedule_summary}</div></div>
        </div>
      )}

      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Sequence Steps</h2>
          {steps.length > 0 && <div className="flex gap-1">{steps.map((_, i) => <button key={i} type="button" onClick={() => setPreviewStep(i)} data-testid={`preview-step-${i}`} className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest transition-colors ${previewStep === i ? "border-white bg-zinc-900 text-white" : "border-zinc-800 text-zinc-500 hover:text-white"}`}>Step {i + 1}</button>)}</div>}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {steps.map((step) => (
            <div key={step.step_number} className="cf-card p-5" data-testid={`step-preview-${step.step_number}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// step {step.step_number + 1} {step.step_number === 0 ? "/ initial" : `/ +${step.day_offset} day${step.day_offset === 1 ? "" : "s"}`}</span>
                <span className="cf-badge">{step.body_type === "html" ? <><Code2 size={10} className="inline" /> HTML</> : <><AlignLeft size={10} className="inline" /> TEXT</>}</span>
              </div>
              <div className="text-white font-medium mb-1">{step.subject}</div>
              {step.subject_b && <div className="text-zinc-500 text-sm mb-1">B: {step.subject_b}</div>}
              <pre className="text-zinc-400 whitespace-pre-wrap font-sans text-xs mt-3 border-t border-zinc-800 pt-3 max-h-24 overflow-hidden">{step.body}</pre>
            </div>
          ))}
        </div>
        {steps[previewStep] && <div className="mt-6"><EmailPreview subject={steps[previewStep].subject} body={steps[previewStep].body} bodyType={steps[previewStep].body_type || "text"} testid="campaign-detail-preview" /></div>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-zinc-800 border border-zinc-800 mb-10">
        <Stat label="Total" value={s.total || 0} testid="stat-total" />
        <Stat label="Sent" value={s.sent || 0} testid="stat-sent" />
        <Stat label="Scheduled" value={s.scheduled || 0} testid="stat-scheduled" />
        <Stat label="Delivered" value={s.delivered || 0} testid="stat-delivered" />
        <Stat label="Opened" value={s.opened || 0} testid="stat-opened" />
        <Stat label="Clicked" value={s.clicked || 0} testid="stat-clicked" />
        <Stat label="Replied" value={s.replied || 0} testid="stat-replied" />
      </div>

      {(s.bounced > 0 || s.spam > 0 || s.failed > 0) && (
        <div className="grid grid-cols-3 gap-px bg-zinc-800 border border-zinc-800 mb-10">
          <Stat label="Failed" value={s.failed || 0} accent={s.failed > 0} testid="stat-failed" />
          <Stat label="Bounced" value={s.bounced || 0} accent={s.bounced > 0} testid="stat-bounced" />
          <Stat label="Spam" value={s.spam || 0} accent={s.spam > 0} testid="stat-spam" />
        </div>
      )}

      {camp.ab_test_enabled && (
        <div className="mb-10">
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-4">A/B Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <VariantCard label="A" stats={variantStats.A || {}} sent={variantStats.A?.sent || 0} />
            <VariantCard label="B" stats={variantStats.B || {}} sent={variantStats.B?.sent || 0} />
          </div>
        </div>
      )}

      <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-4">Per-Send Detail</h2>
      <div className="cf-card overflow-x-auto">
        <table className="cf-data-table" style={{ minWidth: 1020 }}>
          <colgroup><col style={{ width: "18%" }} /><col style={{ width: "22%" }} /><col style={{ width: "6%" }} />{camp.ab_test_enabled && <col style={{ width: "7%" }} />}<col style={{ width: "11%" }} /><col style={{ width: "8%" }} /><col style={{ width: "8%" }} /><col style={{ width: "8%" }} /><col style={{ width: "8%" }} /><col style={{ width: "10%" }} /></colgroup>
          <thead><tr><th>Contact</th><th>Email</th><th style={{ textAlign: "center" }}>Step</th>{camp.ab_test_enabled && <th style={{ textAlign: "center" }}>Var</th>}<th>Status</th><th style={{ textAlign: "center" }}>Deliv</th><th style={{ textAlign: "center" }}>Open</th><th style={{ textAlign: "center" }}>Click</th><th style={{ textAlign: "center" }}>Reply</th><th style={{ textAlign: "right" }}>&middot;</th></tr></thead>
          <tbody>
            {sends.length === 0 && <tr><td colSpan={camp.ab_test_enabled ? 10 : 9} className="text-center text-zinc-600 py-10 font-mono text-xs uppercase">// not sent yet</td></tr>}
            {sends.map((row) => (
              <tr key={row.id} data-testid={`send-row-${row.id}`}>
                <td title={row.contact_name}><span className="text-white truncate block">{row.contact_name}</span></td>
                <td title={row.contact_email}><span className="font-mono text-xs text-zinc-400 truncate block">{row.contact_email}</span></td>
                <td style={{ textAlign: "center" }} className="font-mono text-xs">{(row.step_number ?? 0) + 1}</td>
                {camp.ab_test_enabled && <td style={{ textAlign: "center" }}><span className="cf-badge cf-badge-accent">{row.variant || "A"}</span></td>}
                <td><span className={`cf-badge ${row.status === "sent" ? "cf-badge-success" : row.status === "scheduled" ? "cf-badge-warn" : "cf-badge-accent"}`}>{row.status}</span></td>
                <td style={{ textAlign: "center" }}>{row.delivered ? <Check size={14} className="inline text-emerald-500" /> : <span className="text-zinc-700">\u2014</span>}</td>
                <td style={{ textAlign: "center" }}>{row.opened ? <Check size={14} className="inline text-emerald-500" /> : <span className="text-zinc-700">\u2014</span>}</td>
                <td style={{ textAlign: "center" }}>{row.clicked ? <Check size={14} className="inline text-emerald-500" /> : <span className="text-zinc-700">\u2014</span>}</td>
                <td style={{ textAlign: "center" }}>{row.replied ? <Check size={14} className="inline text-emerald-500" /> : <span className="text-zinc-700">\u2014</span>}</td>
                <td style={{ textAlign: "right" }}>{!row.replied && row.status === "sent" && <button onClick={() => onMarkReplied(row.id)} data-testid={`mark-replied-${row.id}`} className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">Mark replied</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ScheduleCampaignDialog key={scheduleOpen ? `open-${camp.id}` : "closed"} open={scheduleOpen} onOpenChange={setScheduleOpen} onSchedule={onSchedule} initial={camp.schedule} busy={scheduling} />
    </div>
  );
}
